/**
 * Color utility functions for dynamic merchant theming
 */

/**
 * Adjust color lightness
 * @param {string} hex - Hex color code
 * @param {number} percent - Percentage to lighten (positive) or darken (negative)
 * @returns {string} Adjusted hex color
 */
export function adjustColorLightness(hex, percent) {
  // Remove # if present
  hex = hex.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Adjust each channel
  const adjust = (channel) => {
    const adjusted = Math.round(channel + (channel * percent / 100));
    return Math.max(0, Math.min(255, adjusted));
  };

  const newR = adjust(r);
  const newG = adjust(g);
  const newB = adjust(b);

  // Convert back to hex
  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}

/**
 * Create gradient string from a single color
 * @param {string} color - Base hex color
 * @param {number} variation - Percentage variation for gradient end (-10 for darker)
 * @returns {string} CSS gradient string
 */
export function createGradient(color, variation = -10) {
  const endColor = adjustColorLightness(color, variation);
  return `linear-gradient(to right, ${color}, ${endColor})`;
}

/**
 * Get color variants for UI elements
 * @param {string} baseColor - Base hex color
 * @returns {object} Object with color variants
 */
export function getColorVariants(baseColor) {
  return {
    base: baseColor,
    hover: adjustColorLightness(baseColor, -10),
    light: adjustColorLightness(baseColor, 90),
    lighter: adjustColorLightness(baseColor, 95),
    dark: adjustColorLightness(baseColor, -20),
    gradient: createGradient(baseColor, -10),
    contrastText: getContrastTextColor(baseColor),
    contrastIcon: getContrastIconColor(baseColor)
  };
}

/**
 * Add alpha channel to hex color
 * @param {string} hex - Hex color code
 * @param {number} alpha - Alpha value 0-1
 * @returns {string} RGBA color string
 */
export function hexToRgba(hex, alpha = 1) {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Calculate the WCAG relative luminance of a hex color.
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 * @param {string} hex - Hex color code (with or without #)
 * @returns {number} Relative luminance (0–1)
 */
export function relativeLuminance(hex) {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const linearize = (c) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

/**
 * Calculate WCAG contrast ratio between two colors.
 * @param {string} hex1 - First hex color
 * @param {string} hex2 - Second hex color
 * @returns {number} Contrast ratio (1–21)
 */
export function contrastRatio(hex1, hex2) {
  const l1 = relativeLuminance(hex1);
  const l2 = relativeLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Return the best text color (white or dark gray) for a given background,
 * ensuring WCAG 2.1 AA contrast (≥ 4.5:1 for normal text, SC 1.4.3).
 * @param {string} bgHex - Background hex color
 * @returns {string} '#ffffff' or '#111827' (Tailwind gray-900)
 */
export function getContrastTextColor(bgHex) {
  const WHITE = '#ffffff';
  const DARK = '#111827'; // Tailwind gray-900
  return contrastRatio(bgHex, WHITE) >= 4.5 ? WHITE : DARK;
}

/**
 * Return the best icon/UI-component color (white or dark gray) for a given
 * background, using the WCAG 2.1 AA non-text contrast threshold (≥ 3:1, SC 1.4.11).
 * @param {string} bgHex - Background hex color
 * @returns {string} '#ffffff' or '#111827' (Tailwind gray-900)
 */
export function getContrastIconColor(bgHex) {
  const WHITE = '#ffffff';
  const DARK = '#111827'; // Tailwind gray-900
  return contrastRatio(bgHex, WHITE) >= 3 ? WHITE : DARK;
}
